import React, { useState, useRef, useEffect } from 'react';
import styles from './NonLiveTranscription.module.css';
import CustomTooltip from '../Tooltip/Tooltip';
import { AudioPitchAnalyzer } from '../../utils/audioPitchAnalyzer';

const NonLiveTranscription = ({
    isRecording,
    mediaRecorderRef,
    onTranscriptUpdate,
    selectedAudioDevice,
    selectedVideoDevice,
    selectedLanguage,
    useVideo,
    advanceAnalytics
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [transcriptData, setTranscriptData] = useState(null);
    const apiKey = process.env.REACT_APP_SPEECHMATICS_API_KEY;
    const chunksRef = useRef([]);
    const recordingStartTimeRef = useRef(null);
    useEffect(() => {
        // Clear chunks when recording starts
        if (isRecording) {
            chunksRef.current = [];
        }
    }, [isRecording]);

    useEffect(() => {
        if (mediaRecorderRef.current) {
            const recorder = mediaRecorderRef.current;

            const handleDataAvailable = (event) => {
                if (event.data && event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };

            const handleStop = async () => {
                try {
                    if (chunksRef.current.length === 0) {
                        console.warn('No audio data recorded');
                        return;
                    }

                    // Create blob with explicit MIME type
                    const audioBlob = new Blob(chunksRef.current, {
                        type: 'audio/webm;codecs=opus'
                    });

                    if (!audioBlob || audioBlob.size === 0) {
                        throw new Error('Invalid recording: Empty blob');
                    }

                    // Check if user is on Student plan (advanceAnalytics is false)
                    if (!advanceAnalytics) {
                        // For Student plan, just update with the audioBlob but no transcript
                        onTranscriptUpdate({
                            fullText: 'Trasncript available in professional plan',
                            segments: [],
                        });
                        return;
                    }

                    // Process the recording only for non-Student plans
                    await processRecording(audioBlob);

                } catch (error) {
                    console.error('Error in recording stop handler:', error);
                    alert('Failed to process recording. Please try again.');
                }
            };

            // Add event listeners
            recorder.addEventListener('dataavailable', handleDataAvailable);
            recorder.addEventListener('stop', handleStop);

            // Cleanup
            return () => {
                recorder.removeEventListener('dataavailable', handleDataAvailable);
                recorder.removeEventListener('stop', handleStop);
            };
        }
        // eslint-disable-next-line
    }, [mediaRecorderRef.current]);

    const clearTranscript = () => {
        setTranscriptData(null);
        onTranscriptUpdate({
            fullText: '',
            segments: []
        });
    };

    const createMediaRecorder = async (stream) => {
        const options = {
            mimeType: 'audio/webm;codecs=opus',
            bitsPerSecond: 128000
        };

        const recorder = new MediaRecorder(stream, options);

        // Configure event handlers
        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
                chunksRef.current.push(event.data);
            }
        };

        recorder.onstart = () => {
            chunksRef.current = [];
            recordingStartTimeRef.current = Date.now();
            setTranscriptData(null);
        };

        recorder.onstop = async () => {
            try {
                if (chunksRef.current.length === 0) {
                    console.warn('No audio data recorded');
                    return;
                }

                // Create blob with explicit MIME type
                const audioBlob = new Blob(chunksRef.current, {
                    type: 'audio/webm;codecs=opus'
                });

                if (!audioBlob || audioBlob.size === 0) {
                    throw new Error('Invalid recording: Empty blob');
                }

                await processRecording(audioBlob);
            } catch (error) {
                console.error('Error in recording stop handler:', error);
                alert('Failed to process recording. Please try again.');
            }
        };

        return recorder;
    };

    useEffect(() => {
        const initializeRecorder = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        deviceId: selectedAudioDevice ? { exact: selectedAudioDevice } : undefined,
                        sampleRate: 44100,
                        channelCount: 2,
                        echoCancellation: true,
                        noiseSuppression: true
                    },
                    video: useVideo ? {
                        deviceId: selectedVideoDevice ? { exact: selectedVideoDevice } : undefined,
                        width: { ideal: 1280, max: 1280 },
                        height: { ideal: 720, max: 720 },
                        facingMode: "user",
                        frameRate: { max: 30 }
                    } : false
                });

                const recorder = await createMediaRecorder(stream);
                mediaRecorderRef.current = recorder;
            } catch (error) {
                console.error('Error initializing MediaRecorder:', error);
            }
        };

        if (!mediaRecorderRef.current) {
            initializeRecorder();
        }

        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
        };
        // eslint-disable-next-line
    }, [selectedAudioDevice, selectedVideoDevice]);


    const processRecording = async (audioBlob) => {
        setIsProcessing(true);
        try {
            // Create audio context and analyzer for the recorded audio
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const analyzer = new AudioPitchAnalyzer();

            // Convert blob to ArrayBuffer
            const arrayBuffer = await audioBlob.arrayBuffer();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

            // Process audio in chunks to get pitch data
            const sampleRate = audioBuffer.sampleRate;
            const pitchData = [];
            const chunkSize = 2048;

            for (let i = 0; i < audioBuffer.length; i += chunkSize) {
                const chunk = new Float32Array(chunkSize);
                audioBuffer.copyFromChannel(chunk, 0, i);
                const pitch = analyzer.getPitch(chunk, sampleRate);
                pitchData.push(pitch);
            }

            // Rest of the existing Speechmatics API call...
            const formData = new FormData();
            formData.append('data_file', audioBlob, 'recording.webm');
            formData.append('config', JSON.stringify({
                type: 'transcription',
                transcription_config: {
                    language: selectedLanguage,
                    enable_entities: true,    // Enable word-level metadata
                    diarization: "none"       // Disable speaker diarization to save cost
                }
            }));

            const response = await fetch('https://asr.api.speechmatics.com/v2/jobs/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${await response.text()}`);
            }

            const jobData = await response.json();
            const transcript = await pollTranscriptionJob(jobData.id);

            // Calculate base timestamp
            const recordingBaseTime = recordingStartTimeRef.current;

            // Process transcript with pitch data
            const processedTranscript = {
                fullText: transcript.results
                    .filter(result => result.type === 'word')
                    .map(result => result.alternatives[0].content)
                    .join(' '),
                segments: transcript.results
                    .filter(result => result.type === 'word')
                    .map((result, index) => {
                        const timePosition = Math.floor((result.start_time * sampleRate) / chunkSize);
                        const segment = {
                            content: result.alternatives[0].content,
                            confidence: result.alternatives[0].confidence || 1,
                            tags: [
                                ...(result.alternatives[0].tags || []),
                                ...(result.alternatives[0].confidence < 0.75 ? ['low_clarity'] : [])
                            ],
                            type: result.type,
                            timestamp: recordingBaseTime + (result.start_time * 1000),
                            pitch: pitchData[timePosition] || null
                        };
                        return segment;
                    })
            };

            setTranscriptData(processedTranscript);
            onTranscriptUpdate(processedTranscript);

        } catch (error) {
            console.error('Error processing recording:', error);
            alert(`Failed to process recording: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    useEffect(() => {
        if (isRecording) {
            recordingStartTimeRef.current = Date.now();
        }
    }, [isRecording]);

    const pollTranscriptionJob = async (jobId) => {
        const pollInterval = 2000;
        const maxAttempts = 30;
        let attempt = 0;

        while (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));

            try {
                const jobResponse = await fetch(
                    `https://asr.api.speechmatics.com/v2/jobs/${jobId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${apiKey}`
                        }
                    }
                );

                if (!jobResponse.ok) {
                    throw new Error(`Job status check failed: ${jobResponse.statusText}`);
                }

                const jobStatus = await jobResponse.json();

                if (jobStatus.job.status === 'failed') {
                    throw new Error(`Transcription failed: ${JSON.stringify(jobStatus.job.error || {})}`);
                }

                if (jobStatus.job.status === 'done') {
                    return await fetchTranscript(jobId);
                }
            } catch (error) {
                console.error('Error checking job status:', error);
                throw error;
            }

            attempt++;
        }

        throw new Error('Transcription timed out');
    };

    const fetchTranscript = async (jobId) => {
        const response = await fetch(
            `https://asr.api.speechmatics.com/v2/jobs/${jobId}/transcript`,
            {
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Accept': 'application/json'
                }
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to fetch transcript: ${response.statusText}`);
        }

        return await response.json();
    };

    const renderTranscriptSegment = (segment, index) => {
        if (segment.tags?.includes('disfluency')) {
            return (
                <CustomTooltip text="Filler word" key={index} isSegment={true}>
                    <span className={styles.disfluency}>
                        {segment.content}{' '}
                    </span>
                </CustomTooltip>
            );
        } else if (segment.tags?.includes('profanity')) {
            return (
                <CustomTooltip text="Profanity" key={index} isSegment={true}>
                    <span className={styles.profanity}>
                        {segment.content}{' '}
                    </span>
                </CustomTooltip>
            );
        } else if (segment.tags?.includes('low_clarity')) {
            return (
                <CustomTooltip text={`Low clarity (${(segment.confidence * 100).toFixed(1)}%)`} key={index} isSegment={true}>
                    <span className={styles.lowClarity}>
                        {segment.content}{' '}
                    </span>
                </CustomTooltip>
            );
        }
        return <span key={index}>{segment.content} </span>;
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.transcriptSection}>
                    <div className={`${styles.transcriptContent} ${useVideo ? styles.withVideo : ''}`}>
                        {isProcessing ? (
                            <div className={styles.processingMessage}>
                                Processing your recording...
                                <div className={styles.processingSpinner}></div>
                            </div>
                        ) : transcriptData ? (
                            <div className={styles.finalTranscript}>
                                {transcriptData.segments.map((segment, index) =>
                                    renderTranscriptSegment(segment, index)
                                )}
                            </div>
                        ) : (
                            <div className={styles.placeholderText}>
                                {isRecording ? "Recording in progress..." :
                                    !advanceAnalytics && !isRecording ?
                                        "Click Save and Analyze button to save your recording" :
                                        "Your transcript will appear here when you complete practice."
                                }
                            </div>
                        )}
                    </div>
                    <div className={styles.transcriptActions}>
                        <div className={styles.statusBar}>
                            <div className={styles.legend}>
                                {selectedLanguage === 'en' && (
                                    <>
                                        <span className={styles.legendItem}>
                                            <span className={`${styles.statusDot} ${styles.disfluencyDot}`}></span>
                                            Filler Words
                                        </span>
                                        <span className={styles.legendItem}>
                                            <span className={`${styles.statusDot} ${styles.profanityDot}`}></span>
                                            Profanity
                                        </span>
                                    </>
                                )}
                                <span className={styles.legendItem}>
                                    <span className={`${styles.statusDot} ${styles.lowClarityDot}`}></span>
                                    Low Clarity
                                </span>
                            </div>
                        </div>
                        <button
                            className={styles.clearButton}
                            onClick={clearTranscript}
                            disabled={isRecording || !transcriptData}
                        >
                            Clear <span className={styles.clearIcon}>Transcript</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NonLiveTranscription;